/**
 * Serbest meslek makbuzu hesabı.
 * Sisteme girilen tutar = Net Tutar (net tahsilat).
 *
 * Standart oranlar (örnek makbuz):
 * - G.V. stopaj %20
 * - KDV %20
 * - KDV tevkifat %50 (KDV üzerinden)
 *
 * Net Tutar = Net Ücret + Tahsil Edilen KDV
 *           = Brüt×(1-0.20) + Brüt×0.20×(1-0.50)
 *           = Brüt×0.90
 * ⇒ Brüt = Net Tutar / 0.90
 */

export const MAKBUZ_GV_STOPAJ_ORANI = 0.20;
export const MAKBUZ_KDV_ORANI = 0.20;
export const MAKBUZ_KDV_TEVKIFAT_ORANI = 0.50;

export function roundMoney(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/** 46.200,21 / 46200,21 / 46200.21 */
export function parseTRAmount(value) {
  if (value == null || value === '') return 0;
  const s = String(value).trim().replace(/\s/g, '');
  if (!s) return 0;
  if (s.includes(',') && s.includes('.')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  }
  if (s.includes(',')) {
    return parseFloat(s.replace(',', '.')) || 0;
  }
  return parseFloat(s) || 0;
}

export function formatMakbuzTL(amount) {
  const n = Number(amount) || 0;
  return n.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildMakbuzKalemleri(ucret) {
  const brutUcret = roundMoney(ucret);
  const kdv = roundMoney(brutUcret * MAKBUZ_KDV_ORANI);
  const gvStopaj = roundMoney(brutUcret * MAKBUZ_GV_STOPAJ_ORANI);
  const netUcret = roundMoney(brutUcret - gvStopaj);
  const kdvTevkifat = roundMoney(kdv * MAKBUZ_KDV_TEVKIFAT_ORANI);
  const tahsilEdilenKdv = roundMoney(kdv - kdvTevkifat);
  const brutKdvDahil = roundMoney(brutUcret + kdv);
  const netTahsilat = roundMoney(netUcret + tahsilEdilenKdv);

  return {
    brutUcret,
    kdv,
    gvStopaj,
    netUcret,
    kdvTevkifat,
    tahsilEdilenKdv,
    odenecekKdv: tahsilEdilenKdv,
    brutKdvDahil,
    netTahsilat,
    netTutar: netTahsilat,
  };
}

/**
 * Sigorta ödeme detayındaki Mahkeme/İcra Kararı (KDV dahil vekalet) üzerinden hesap.
 * Ücret = Karar / (1 + KDV oranı)
 */
export function calculateFromMahkemeKarari(kararTutari) {
  const karar = roundMoney(kararTutari);
  const ucret = roundMoney(karar / (1 + MAKBUZ_KDV_ORANI));
  return {
    mahkemeKarari: karar,
    ...buildMakbuzKalemleri(ucret),
  };
}

/** Makbuzdaki Brüt Ücret (KDV hariç) üzerinden hesap */
export function calculateFromBrutUcret(brut) {
  return {
    mahkemeKarari: roundMoney(Number(brut) * (1 + MAKBUZ_KDV_ORANI)),
    ...buildMakbuzKalemleri(brut),
  };
}

/** Net tutardan brüt ve vergi kalemlerini hesaplar */
export function calculateFromNetTutar(netTutar) {
  const net = Number(netTutar) || 0;
  const netCarpan =
    (1 - MAKBUZ_GV_STOPAJ_ORANI) +
    MAKBUZ_KDV_ORANI * (1 - MAKBUZ_KDV_TEVKIFAT_ORANI); // 0.90

  const brutUcret = netCarpan > 0 ? net / netCarpan : 0;
  const row = buildMakbuzKalemleri(brutUcret);
  return {
    ...row,
    netTutar: net,
    netTahsilat: net,
  };
}

/** Birden fazla makbuzun (net tutar listesi) aylık özeti */
export function summarizeMakbuzNetTutarlar(netTutarList) {
  return (netTutarList || []).reduce(
    (acc, net) => {
      const row = calculateFromNetTutar(net);
      acc.count += 1;
      acc.netTutar += row.netTutar;
      acc.brutUcret += row.brutUcret;
      acc.gvStopaj += row.gvStopaj;
      acc.netUcret += row.netUcret;
      acc.kdv += row.kdv;
      acc.kdvTevkifat += row.kdvTevkifat;
      acc.tahsilEdilenKdv += row.tahsilEdilenKdv;
      acc.odenecekKdv += row.odenecekKdv;
      return acc;
    },
    {
      count: 0,
      netTutar: 0,
      brutUcret: 0,
      gvStopaj: 0,
      netUcret: 0,
      kdv: 0,
      kdvTevkifat: 0,
      tahsilEdilenKdv: 0,
      odenecekKdv: 0
    }
  );
}

/*
 * MAKBUZUN KENDİ KDV'Sİ (2026-10-07)
 * ---------------------------------
 * Yukarıdaki geri hesap her makbuzda %20 stopaj ve KDV'nin yarısının tevkif
 * edildiğini varsayar (sigorta şirketine kesilen makbuz). Şahsa kesilen
 * makbuzda stopaj ve tevkifat yoktur; orada ödenecek KDV'nin tamamı büroya
 * düşer ve varsayım KDV'yi eksik gösterir. Makbuz yüklenirken makbuzdaki
 * "tahsil edilen KDV" okunup kaydediliyor; varsa hesap onu kullanır.
 */

/* Bilinen makbuz kalıpları: stopaj ve KDV tevkifatı var / yok. Brüt
   okunmamışsa, net / ödenecek KDV oranına en yakın kalıptan bulunur. */
const KALIPLAR = [
  { stopaj: MAKBUZ_GV_STOPAJ_ORANI, tevkifat: MAKBUZ_KDV_TEVKIFAT_ORANI }, // sigorta / şirket, tevkifatlı
  { stopaj: MAKBUZ_GV_STOPAJ_ORANI, tevkifat: 0 },                       // şirket, tevkifatsız
  { stopaj: 0, tevkifat: 0 },                                            // şahıs
  { stopaj: 0, tevkifat: MAKBUZ_KDV_TEVKIFAT_ORANI },
];

/**
 * Bir makbuzun kalemleri. odenecekKdv (makbuzdan okunan / girilen) varsa o
 * kullanılır, yoksa net tutardan varsayılan oranlarla tahmin edilir.
 * @param {{ net: number, odenecekKdv?: number|null, brutUcret?: number|null }} m
 */
export function makbuzKalemleri({ net, odenecekKdv, brutUcret }) {
  const n = roundMoney(Number(net) || 0);
  const kdvOkundu = odenecekKdv != null && odenecekKdv !== '' && Number.isFinite(Number(odenecekKdv));
  if (!kdvOkundu) return { ...calculateFromNetTutar(n), tahmini: true };

  const odenecek = roundMoney(Number(odenecekKdv));
  let brut = brutUcret != null && Number(brutUcret) > 0 ? roundMoney(Number(brutUcret)) : null;
  if (brut == null) {
    // Net ve ödenecek KDV'ye en uyan kalıp: net = brüt×(1−stopaj) + brüt×KDV×(1−tevkifat)
    let enIyi = null;
    for (const k of KALIPLAR) {
      const kdvPayi = MAKBUZ_KDV_ORANI * (1 - k.tevkifat);
      const b = n / ((1 - k.stopaj) + kdvPayi);
      const fark = Math.abs(b * kdvPayi - odenecek);
      if (!enIyi || fark < enIyi.fark) enIyi = { b, fark };
    }
    brut = roundMoney(enIyi ? enIyi.b : n);
  }
  const kdv = roundMoney(brut * MAKBUZ_KDV_ORANI);
  const netUcret = roundMoney(n - odenecek);
  return {
    brutUcret: brut,
    kdv,
    gvStopaj: roundMoney(Math.max(0, brut - netUcret)),
    netUcret,
    kdvTevkifat: roundMoney(Math.max(0, kdv - odenecek)),
    tahsilEdilenKdv: odenecek,
    odenecekKdv: odenecek,
    brutKdvDahil: roundMoney(brut + kdv),
    netTahsilat: n,
    netTutar: n,
    tahmini: false,
  };
}

/** Belge kaydından (Document) makbuz kalemleri. */
export const belgeMakbuzKalemleri = (d) => makbuzKalemleri({
  net: parseFloat(d?.Tutar ?? d?.tutar ?? 0) || 0,
  odenecekKdv: d?.OdenecekKdv ?? d?.odenecekKdv ?? null,
  brutUcret: d?.BrutUcret ?? d?.brutUcret ?? null,
});

/** Makbuz belgelerinin (Document) toplamı; tahmini: KDV'si makbuzdan okunmamış makbuz sayısı. */
export function summarizeMakbuzBelgeleri(belgeler) {
  const bas = {
    count: 0, netTutar: 0, brutUcret: 0, gvStopaj: 0, netUcret: 0, kdv: 0,
    kdvTevkifat: 0, tahsilEdilenKdv: 0, odenecekKdv: 0, tahmini: 0,
  };
  const t = (belgeler || []).reduce((acc, d) => {
    const r = belgeMakbuzKalemleri(d);
    acc.count += 1;
    for (const k of ['netTutar', 'brutUcret', 'gvStopaj', 'netUcret', 'kdv', 'kdvTevkifat', 'tahsilEdilenKdv', 'odenecekKdv']) acc[k] += r[k];
    if (r.tahmini) acc.tahmini += 1;
    return acc;
  }, bas);
  for (const k of ['netTutar', 'brutUcret', 'gvStopaj', 'netUcret', 'kdv', 'kdvTevkifat', 'tahsilEdilenKdv', 'odenecekKdv']) t[k] = roundMoney(t[k]);
  return t;
}
